import './AnnotationNavigation.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faArrowAltCircleLeft, faBan, faShare, faUserSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { AnnotationNavigationButton } from '../../../../modules/common/annotationNavigationButton/AnnotationNavigationButton';
import { EditorMode } from '../../../../modules/editor/models/EditorModes';
import { ValidSaveIcon } from '../../../components/icons/ValidSaveIcon';
import { SubmitAnnotationButtonContainer } from '../containers/SubmitAnnotationButton.container';
import { RequestClarificationButtonContainer } from '../submodules/clarifications/containers/RequestClarificationButton.container';
import { DiscardAnnotationConfirmationModal } from './DiscardAnnotationConfirmationModal';
import { QuitAnnotationsModal } from './QuitAnnotationsModal';

library.add(faUserSlash, faBan, faArrowAltCircleLeft, faShare);

interface IAnnotationNavigationComponentProps {
  onSubmit: () => Promise<void>;
  onSave: () => Promise<void>;
  onSaveAsDraft: () => Promise<void>;
  onAbandonDraft: () => void;
  onSkip: () => Promise<void>;
  onDiscardConfirmed: () => Promise<void>;
  onDiscardCanceled: () => void;
  onDiscard: () => void;
  onCancel: () => Promise<void>;
  onCancelCancel: () => void;
  onConfirmCancel: () => Promise<void>;

  disabled: boolean;
  showDiscardModal: boolean;
  showCancelModal: boolean;

  hideSkip: boolean;
  hideDraft: boolean;
  hideAbandonDraft: boolean;

  canCancel: boolean;
  canDiscard: boolean;
  canSave: boolean;
  canSubmit: boolean;
  editorMode: EditorMode;
}

export const AnnotationNavigation = observer((props: IAnnotationNavigationComponentProps): ReactElement => {
  const { t } = useTranslator();
  return (
    <div className='annotation-navigation standard'>
      {props.canSubmit && (
        <SubmitAnnotationButtonContainer
          onSubmit={props.onSubmit}
          disabled={props.disabled}
        />
      )}
      {props.canSave && (
        <AnnotationNavigationButton
          size='md'
          loaderKey='save-annotation-button'
          disabled={props.disabled}
          onClick={props.onSave}
          title={t('save_tooltip')}
          startDecorator={<ValidSaveIcon />}
        >
          {t('save')}
        </AnnotationNavigationButton>
      )}
      {props.canDiscard && (
        <AnnotationNavigationButton
          size='md'
          color='danger'
          id='discard-annotation'
          loaderKey='discard-annotation-button'
          disabled={props.disabled}
          onClick={props.onDiscard}
          title={t('discard_tooltip')}
          startDecorator={<FontAwesomeIcon icon={faBan} />}
        >
          {t('discard')}
        </AnnotationNavigationButton>
      )}
      <RequestClarificationButtonContainer
        disabled={props.disabled}
        editorMode={props.editorMode}
      />

      {!props.hideSkip && (
        <AnnotationNavigationButton
          size='md'
          loaderKey='skip-annotation-button'
          disabled={props.disabled}
          onClick={props.onSkip}
          title={t('skip_tooltip')}
          color='neutral'
          variant='outlined'
          startDecorator={<FontAwesomeIcon icon={faShare} />}
        >
          {t('skip')}
        </AnnotationNavigationButton>
      )}
      {!props.hideAbandonDraft && (
        <AnnotationNavigationButton
          size='md'
          loaderKey='abandon-annotation-button'
          disabled={props.disabled}
          onClick={props.onAbandonDraft}
          title={t('abandon_draft_tooltip')}
          startDecorator={<FontAwesomeIcon icon={faUserSlash} />}
        >
          {t('abandon_draft')}
        </AnnotationNavigationButton>
      )}
      {!props.hideDraft && (
        <AnnotationNavigationButton
          size='md'
          loaderKey='save-draft-button'
          disabled={props.disabled}
          onClick={props.onSaveAsDraft}
          title={t('save_draft_tooltip')}
          color='neutral'
          variant='outlined'
          startDecorator={<ValidSaveIcon />}
        >
          {t('save_as_draft')}
        </AnnotationNavigationButton>
      )}

      {props.canCancel && (
        <AnnotationNavigationButton
          size='md'
          color='neutral'
          variant='outlined'
          loaderKey='cancel-annotation-button'
          disabled={props.disabled}
          onClick={props.onCancel}
          title={t('cancel_tooltip')}
          startDecorator={<FontAwesomeIcon icon={faArrowAltCircleLeft} />}
        >
          {t('cancel')}
        </AnnotationNavigationButton>
      )}
      <QuitAnnotationsModal
        isOpen={props.showCancelModal}
        onCancel={props.onCancelCancel}
        onConfirm={props.onConfirmCancel}
      />
      <DiscardAnnotationConfirmationModal
        showDiscardModal={props.showDiscardModal}
        onDiscardCancel={props.onDiscardCanceled}
        onDiscardConfirm={props.onDiscardConfirmed}
      />
    </div>
  );
});
